/**
 * Gmail <-> Googlemail equivalence for client-side email searches.
 *
 * `gmail.com` and `googlemail.com` are the same Google mailbox - Google treats
 * the two domains as interchangeable aliases for one account. So a search for
 * an address at one domain should also match the equivalent address at the
 * other.
 *
 * `emailSearchVariants` expands a lowercased search term into the set of
 * equivalent variants. A term that does not contain either Google domain is
 * returned unchanged (single variant), so non-Google searches keep their exact
 * current behaviour.
 *
 * `emailMatches` is a convenience helper: does `haystack` contain the term,
 * treating the two Google domains as equivalent?
 */

const GMAIL = '@gmail.com';
const GOOGLEMAIL = '@googlemail.com';

/**
 * Return the lowercased search variants for `term`. If the term contains an
 * `@gmail.com` or `@googlemail.com` domain, the swapped-domain equivalent is
 * appended. Otherwise a single-element list is returned. Order is stable
 * (original first) and duplicates are removed.
 */
export function emailSearchVariants(term: string): string[] {
  const base = (term || '').trim().toLowerCase();
  if (!base) return [];
  const variants = [base];
  if (base.includes(GMAIL)) {
    variants.push(base.replace(GMAIL, GOOGLEMAIL));
  } else if (base.includes(GOOGLEMAIL)) {
    variants.push(base.replace(GOOGLEMAIL, GMAIL));
  }
  return Array.from(new Set(variants));
}

/**
 * True if `haystack` contains the search `term`, treating gmail.com and
 * googlemail.com as equivalent. Both sides are compared case-insensitively.
 */
export function emailMatches(haystack: string | undefined | null, term: string): boolean {
  if (!haystack) return false;
  const hay = haystack.toLowerCase();
  return emailSearchVariants(term).some((v) => hay.includes(v));
}
